/**
 * API Utils
 */

/**
 * Returns a valid API URL
 *
 * It appends the current user's access_token to the url
 *
 * @param {string}   url           Required API url with no domain or GET params
 * @param {Object}   [params]      GET params (also accepts an array where each item is an object: {key: '', value: ''})
 * @param {string}   [service=api] Which service to use. (api | comments_api | files_api | activity)
 * @returns {string}
 */
function getUrl(url, params, service) {
	if (!service)
		service = "api";
	let apiUrl = LEO.urls[service];

	// Add requested endpoint
	apiUrl += url;

	// Add GET params (if any)
	if (params) {
		if (Array.isArray(params)) {
			// Convert `[{key: "a", value: 1}, {key: "b", value: 2}]` to `{a: 1, b: 2}`
			params = params.reduce((acc, cur) => Object.assign(acc, {[cur.key]: cur.value}), {});
		}

		apiUrl += (apiUrl.includes("?") ? "&" : "?") + (new URLSearchParams(params)).toString();
	}

	return apiUrl;
}

async function call(method, url, params, extraHeaders = {}, fetchOptions = {}) {
	let response;

	// eslint-disable-next-line no-prototype-builtins
	const isSimpleObject = typeof params === "object" && Object.getPrototypeOf(params).isPrototypeOf(Object);

	const headers = Object.assign({
		"X-PN-CLIENT": "web/" + LEO.version
	}, extraHeaders);
	if (LEO.api !== undefined && LEO.api.user_access_token !== undefined && LEO.api.user_access_token)
		headers["X-ACCESS-TOKEN"] = LEO.api.user_access_token;

	switch (method) {
		case "GET": {
			const processedParams = isSimpleObject // is it a simple object?
				? new URLSearchParams(params)
				: params;

			if (processedParams)
				url += (url.includes("?") ? "&" : "?") + processedParams.toString();

			try {
				response = await fetch(url, Object.assign(fetchOptions, {method, headers}));
			} catch (e) {
				e.errorType = "fetch";
				throw e;
			}
			break;
		}
		default: {
			let body = params;
			if (isSimpleObject || Array.isArray(params)) {
				body = JSON.stringify(params);
				headers["Content-Type"] = "application/json";
			}
			try {
				response = await fetch(url, Object.assign(fetchOptions, {method, headers, body}));
			} catch (e) {
				e.errorType = "fetch";
				throw e;
			}
			break;
		}
	}

	let data = null;
	try {
		data = await response.json();
	} catch (e) {}

	if (response.status >= 200 && response.status < 300)
		return {data};

	// Go to login page (through logout) if the access token is expired (i.e. we're opening the site after a long time)
	if (
		response.status === 401
		&& data && data.error
		&& data.error.code === 4002 // access token is expired
	)
		location.href = "/account/logout";

	const err = new Error("Error on API call (" + response.status + ")");
	err.errorType = "response";
	err.response = response;
	err.response.data = data;
	throw err;
}

export default {
	getUrl: getUrl,
	get:    async (url, params, extraHeaders, fetchOptions) => call("GET", url, params, extraHeaders, fetchOptions),
	post:   async (url, params, extraHeaders, fetchOptions) => call("POST", url, params, extraHeaders, fetchOptions),
	put:    async (url, params, extraHeaders, fetchOptions) => call("PUT", url, params, extraHeaders, fetchOptions),
	patch:  async (url, params, extraHeaders, fetchOptions) => call("PATCH", url, params, extraHeaders, fetchOptions),
	delete: async (url, params, extraHeaders, fetchOptions) => call("DELETE", url, params, extraHeaders, fetchOptions)
};
